import React, { useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import Metrics from './Metrics';
import TeamMembersOverview from './TeamMembersOverview';
import type { MetricsData, MEViewMode, TeamMember } from './types'; // MEViewMode used by useState
import './MEView.css';

export interface MEViewProps {
  /** Metrics for individual / current-user view */
  metrics: MetricsData;
  /** Team members list for the summary (admin) view */
  teamMembers: TeamMember[];
  /** Whether data is still loading */
  isLoading: boolean;
  /** Whether the current user is admin or PTO approver (shows Summary view) */
  isAdmin: boolean;
  /** Sprint start date (from dashboard) used in team overview metrics */
  startDate?: string;
  /** Sprint due date (from dashboard) used in team overview metrics */
  dueDate?: string;
  /** Slot for the Handsontable grid — rendered in the ME view grid area */
  gridSlot?: React.ReactNode;
  /** Callback when a team member row is clicked */
  onMemberClick?: (member: TeamMember) => void;
  /** Callback when view returns to summary after member detail */
  onBackToSummary?: () => void;
  /** Called with the summary export function once TeamMembersOverview is ready */
  onSummaryExportReady?: (fn: () => Promise<void>) => void;
  /** Whether to render the internal back+name header bar (default true) */
  showMemberHeader?: boolean;
}

export interface MEViewHandle {
  backToSummary: () => void;
}

/**
 * MEView — productivity view component.
 *
 * Modes:
 *   "summary" — team overview table (admin/PTOApprover only)
 *   "me"      — individual Metrics sidebar + Grid
 *
 * Mode is driven by internal state only (no visible selector).
 * When a team member is clicked in summary mode the view transitions to "me" and
 * a back button appears.
 */
const MEView = forwardRef<MEViewHandle, MEViewProps>(
  (
    {
      metrics,
      teamMembers,
      isLoading,
      isAdmin,
      startDate = '',
      dueDate = '',
      gridSlot,
      onMemberClick,
      onBackToSummary,
      onSummaryExportReady,
      showMemberHeader = true,
    },
    ref,
  ) =>
  {
    const [mode, setMode] = useState<MEViewMode>(isAdmin ? 'summary' : 'me');
    const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
    const [metricsCollapsed, setMetricsCollapsed] = useState(false);

    const handleMemberClick = useCallback(
      (member: TeamMember) =>
      {
        setSelectedMember(member);
        setMode('me');
        onMemberClick?.(member);
      },
      [onMemberClick],
    );

    const handleBackToSummary = useCallback(() =>
    {
      setSelectedMember(null);
      setMode('summary');
      onBackToSummary?.();
    }, [onBackToSummary]);

    useImperativeHandle(ref, () => ({ backToSummary: handleBackToSummary }), [handleBackToSummary]);

    return (
      <div className="meview-root">
        {/* Back button when drilling into a member from summary */}
        {showMemberHeader && selectedMember && (
          <div className="meview-member-header">
            <button className="meview-back-btn" onClick={handleBackToSummary}>
              ← Back
            </button>
            <span className="meview-member-name">{selectedMember.Name}</span>
          </div>
        )}

        {/* Content */}
        <div className="meview-content">
          {/* Summary view */}
          {mode === 'summary' && !selectedMember && (
            <div className="meview-summary-layout">
              <TeamMembersOverview
                teamMembers={teamMembers}
                metrics={{
                  dueDate: dueDate || metrics.dueDate,
                  startDate: startDate || metrics.startDate,
                }}
                isLoading={isLoading}
                onMemberClick={handleMemberClick}
                onExportReady={onSummaryExportReady}
              />
            </div>
          )}

          {/* ME / individual view */}
          {(mode === 'me' || selectedMember) && (
            <div className="meview-me-layout">
              <div className={`meview-me-metrics-sidebar${metricsCollapsed ? ' collapsed' : ''}`}>
                <Metrics data={metrics} isEnableSkeleton={isLoading} />
              </div>
              {!isLoading && (
                <button
                  className="meview-toggle-btn"
                  onClick={() => setMetricsCollapsed(c => !c)}
                  title={metricsCollapsed ? 'Show metrics' : 'Hide metrics'}
                >
                  <span className={`fa ${metricsCollapsed ? 'fa-caret-right' : 'fa-caret-left'}`} />
                </button>
              )}
              <div className="meview-me-grid">{gridSlot}</div>
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default MEView;
